// public/js/features/pois/poi-locate.js
// Turns a coordinate into a place name using the tenant's POIs and geofences.
//
// Resolution happens AT RUNTIME, never from a name frozen into the record when
// the trip was written. Draw a geofence around a workshop tomorrow and every
// trip that ever started there immediately reads "Workshop XY" — no rebuild, no
// migration.
//
// Precedence, most specific first:
//   1. inside a geofence POLYGON
//   2. inside a geofence RADIUS
//   3. nearest POI within NEARBY_FALLBACK_M (labelled "near …")
//   4. nothing — the caller falls back to coordinates

// How far away a POI may be and still be worth naming when NO geofence covers
// the point. Deliberately generous: most POIs have no geofence drawn yet (18 of
// 221 at the time of writing), and an airport POI sits at the terminal while an
// asset stands 2 km away on the apron — plainly "at MIA", yet 1.1 km from the
// pin. The result is always labelled with its distance and marked inexact, so a
// nearby guess can never read as "inside".
const NEARBY_FALLBACK_M = 5000;

let _pois = null;
let _loadedAt = 0;
const TTL_MS = 5 * 60 * 1000;

const EARTH_R = 6371000;
function metres(aLat, aLon, bLat, bLon) {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat), dLon = rad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2 +
            Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Ray casting against a [[lng,lat], …] ring — same method the backend geofence
// engine uses, so the UI and the alerts agree on what "inside" means.
function inPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Load (and briefly cache) the POIs this user can see. */
export async function loadPlaces(force = false) {
  if (!force && _pois && Date.now() - _loadedAt < TTL_MS) return _pois;
  try {
    const res = await window.authenticatedFetch('api/v1/pois');
    if (!res.ok) throw new Error('pois');
    const data = await res.json();
    _pois = (data.pois || []).filter(p => p.lat != null && p.lng != null);
    _loadedAt = Date.now();
  } catch {
    _pois = _pois || [];   // keep whatever we had; never break the page
  }
  return _pois;
}

/** Drop the cache so a newly drawn geofence shows up without a reload. */
export function invalidatePlaces() { _loadedAt = 0; }

/**
 * @returns {{name: string, exact: boolean, distanceM: number}|null}
 *   exact=true when the point is genuinely inside a geofence.
 */
export function locate(lat, lon, pois = _pois) {
  if (lat == null || lon == null || !Array.isArray(pois) || !pois.length) return null;
  const la = Number(lat), lo = Number(lon);

  let nearest = null, nearestD = Infinity;

  for (const p of pois) {
    const pLat = Number(p.lat), pLng = Number(p.lng);
    const d = metres(la, lo, pLat, pLng);

    const ring = Array.isArray(p.geofencePolygon) && p.geofencePolygon.length >= 3 ? p.geofencePolygon : null;
    if (ring && inPolygon(la, lo, ring)) {
      return { name: p.name || p.shortName || 'Unnamed area', shortName: p.shortName || null, exact: true, distanceM: Math.round(d) };
    }
    const radius = Number(p.geofenceRadiusM);
    if (Number.isFinite(radius) && radius > 0 && d <= radius) {
      return { name: p.name || p.shortName || 'Unnamed area', shortName: p.shortName || null, exact: true, distanceM: Math.round(d) };
    }
    if (d < nearestD) { nearestD = d; nearest = p; }
  }

  if (nearest && nearestD <= NEARBY_FALLBACK_M) {
    return { name: nearest.name || nearest.shortName || 'Unnamed place', shortName: nearest.shortName || null, exact: false, distanceM: Math.round(nearestD) };
  }
  return null;
}
